import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useHomepageSpeakers } from '../../hooks/useSpeakers';
import { utils, Speaker } from '../../services/wordpressApi';
import { proxiedMediaUrl } from '../../services/mediaProxy';
import Button from '../common/Button';
import '../../styles/Hero.css';

const Hero: React.FC = () => {
  // Start rotation immediately (we removed the global splash screen)
  const { currentSpeaker, loading, error, currentSpeakerIndex } = useHomepageSpeakers({ startRotationDelay: 0 });
  const [currentImage, setCurrentImage] = useState<string>('');
  const [imageLoaded, setImageLoaded] = useState(false);
  // Render the hero shell immediately; speaker data/images can hydrate in.
  const [showContent] = useState(true);
  const [textAnimationClass, setTextAnimationClass] = useState<string>('');
  const [textHasAnimatedIn, setTextHasAnimatedIn] = useState(false);
  const [textVisible, setTextVisible] = useState(false);
  const [displayedSpeakerName, setDisplayedSpeakerName] = useState<string>('');
  const [displayedCompanyName, setDisplayedCompanyName] = useState<string>('');
  const [pendingSpeakerName, setPendingSpeakerName] = useState<string>('');
  const [pendingCompanyName, setPendingCompanyName] = useState<string>('');
  const [displayedTextPosition, setDisplayedTextPosition] = useState<string>('top-right');
  const [pendingTextPosition, setPendingTextPosition] = useState<string>('top-right');
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [kenBurnsActive, setKenBurnsActive] = useState(false);
  const [scrollOpacity, setScrollOpacity] = useState(1);
  const imageCache = useRef<{ [key: string]: boolean }>({});
  const previousSpeakerIndex = useRef<number>(-1);

  // NOTE: The site used to intentionally delay hero rendering to match the old splash screen.
  // With the splash removed, we keep the hero visible immediately and let content load in.

  // Handle scroll-based fade effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const viewportHeight = window.innerHeight;
      const scrollPercentage = (scrollY / viewportHeight) * 100;
      
      // Fade from 45% to 70% of viewport height (20% sooner)
      if (scrollPercentage <= 45) {
        setScrollOpacity(1);
      } else if (scrollPercentage >= 70) {
        setScrollOpacity(0);
      } else {
        // Linear interpolation between 45% and 70%
        const fadeRange = 70 - 45; // 25%
        const fadeProgress = (scrollPercentage - 45) / fadeRange;
        const opacity = 1 - fadeProgress;
        setScrollOpacity(Math.max(0, Math.min(1, opacity)));
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Call once to set initial state

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Get speaker image URL
  const getSpeakerImage = (speaker: Speaker | null): string => {
    if (!speaker) return '';
    
    // Prefer the explicit homepage slider image if set, otherwise fallback to featured_media.
    const sliderImageId = typeof (speaker as any).homepage_slider_image_id === 'number' ? (speaker as any).homepage_slider_image_id : 0;
    if (sliderImageId && sliderImageId > 0) {
      return proxiedMediaUrl(sliderImageId, 'large');
    }

    if (speaker.featured_media && speaker.featured_media > 0) {
      return proxiedMediaUrl(speaker.featured_media, 'large');
    }

    // Legacy fallback (avoid using direct URL when possible)
    const speakerImage = utils.getACFField(speaker, 'speaker_image');
    if (speakerImage && speakerImage.url) {
      // NOTE: this exposes the origin URL if used; keep only as last resort.
      return speakerImage.url;
    }
    
    // Fallback to placeholder
    return '/api/placeholder/1200/800';
  };

  // Get speaker biography excerpt (first 50 characters)
  const getBiographyExcerpt = (speaker: Speaker | null): string => {
    if (!speaker) return '';
    
    const biography = utils.getACFField(speaker, 'biography') || speaker.excerpt?.rendered || speaker.content?.rendered || '';
    const cleanText = utils.stripHTML(biography);
    return cleanText.length > 50 ? cleanText.substring(0, 50) + '...' : cleanText;
  };

  // Generate speaker slug from name
  const generateSpeakerSlug = (speaker: Speaker | null): string => {
    if (!speaker) return '';
    
    // Use existing slug if available
    if (speaker.slug) return speaker.slug;
    
    // Special cases for specific speakers
    const name = speaker.title?.rendered || '';
    if (name === 'Danny Meyer') {
      return 'danny-meyer-shake-shack-ceo';
    }
    
    // Generate slug from name
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single
      .trim();
  };



  // Get speaker company name with "The" prefix for certain companies
  const getSpeakerCompany = (speaker: Speaker | null): string => {
    if (!speaker) return '';
    
    // Try ACF field for company
    const company = utils.getACFField(speaker, 'company') || 
                   utils.getACFField(speaker, 'company_name') ||
                   utils.getACFField(speaker, 'organization') ||
                   utils.getACFField(speaker, 'employer');
    
    if (!company) return '';
    
    // Add "The" prefix for certain company names
    const companiesNeedingThe = ['New York Times', 'Washington Post'];
    if (companiesNeedingThe.includes(company)) {
      return `The ${company}`;
    }
    
    return company;
  };

  // Get speaker text position for name/company block
  // Expected values: "top-right", "bottom-right", "top-left", "bottom-left"
  const getSpeakerTextPosition = (speaker: Speaker | null): string => {
    if (!speaker) return 'top-right';

    const raw =
      (speaker as any).homepage_slider_text_position ||
      utils.getACFField(speaker, 'homepage_slider_text_position') ||
      utils.getACFField(speaker, 'homepage_text_position') ||
      utils.getACFField(speaker, 'slide_text_position');

    const val = typeof raw === 'string' ? raw.toLowerCase().trim().replace(/\s+/g, '-') : '';

    const allowed = new Set(['top-right', 'bottom-right', 'top-left', 'bottom-left']);
    return allowed.has(val) ? val : 'top-right';
  };


  // Handle speaker changes
  useEffect(() => {
    if (!currentSpeaker) return undefined;

    const newImageUrl = getSpeakerImage(currentSpeaker);
    const newSpeakerName = currentSpeaker.title?.rendered || 'Speaker Name';
    const newCompanyName = getSpeakerCompany(currentSpeaker);
    const newTextPosition = getSpeakerTextPosition(currentSpeaker);
    const isFirstLoad = previousSpeakerIndex.current === -1;
    const isSpeakerChange = currentSpeakerIndex !== previousSpeakerIndex.current && !isFirstLoad;

    if (isSpeakerChange && !isAnimatingOut) {
      // Store the new content but don't display it yet
      setPendingSpeakerName(newSpeakerName);
      setPendingCompanyName(newCompanyName);
      setPendingTextPosition(newTextPosition);
      
      // Speaker changing - animate current text out first (old text still showing)
      setIsAnimatingOut(true);
      setTextAnimationClass('tracking-out-contract-bck');
      setTextHasAnimatedIn(false);
      setTextVisible(false);
      
      // After text animates out, update displayed content and handle image transition
      setTimeout(() => {
        // Now update to the new text content
        setDisplayedSpeakerName(newSpeakerName);
        setDisplayedCompanyName(newCompanyName);
        setDisplayedTextPosition(newTextPosition);
        setTextAnimationClass('');
        setIsAnimatingOut(false);
        handleImageChange(newImageUrl);
      }, 550); // Wait for out animation to complete
      
    } else if (isFirstLoad) {
      // First load - set text and load the image
      setDisplayedSpeakerName(newSpeakerName);
      setDisplayedCompanyName(newCompanyName);
      setDisplayedTextPosition(newTextPosition);
      setPendingSpeakerName(newSpeakerName);
      setPendingCompanyName(newCompanyName);
      setPendingTextPosition(newTextPosition);
      setPendingTextPosition(newTextPosition);
      setTextHasAnimatedIn(false);
      setTextVisible(false);
      handleImageChange(newImageUrl);
    }

    previousSpeakerIndex.current = currentSpeakerIndex;
    return undefined;
  }, [currentSpeaker, currentSpeakerIndex, isAnimatingOut]);

  // Show text after image loads
  useEffect(() => {
    if (imageLoaded && currentImage && displayedSpeakerName && !textHasAnimatedIn && !isAnimatingOut) {
      const timer = setTimeout(() => {
        setTextAnimationClass('tracking-in-expand-fwd');
        setTextHasAnimatedIn(true);
        
        // Clear animation class after animation completes and set visible
        setTimeout(() => {
          setTextAnimationClass('');
          setTextVisible(true);
        }, 900); // A bit longer than animation duration
      }, 300); // Small delay after image loads
      
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [imageLoaded, currentImage, displayedSpeakerName, textHasAnimatedIn, isAnimatingOut]);

  const handleImageChange = (newImageUrl: string) => {
    // If image is already cached, show it immediately
    if (imageCache.current[newImageUrl]) {
      if (currentImage && currentImage !== newImageUrl) {
        // Fade out current image while keeping Ken Burns active
        setImageLoaded(false);
        setTimeout(() => {
          // Now reset Ken Burns and switch to new image
          setKenBurnsActive(false);
          setCurrentImage(newImageUrl);
          setImageLoaded(true);
          // Start Ken Burns for new image
          setTimeout(() => setKenBurnsActive(true), 50);
        }, 500);
      } else {
        // First load or same image
        setCurrentImage(newImageUrl);
        setImageLoaded(true);
        setTimeout(() => setKenBurnsActive(true), 50);
      }
      return;
    }

    // If changing to a new image, preload it first, then handle transition
    if (currentImage && currentImage !== newImageUrl) {
      // Preload the new image while keeping current one visible
      preloadImage(newImageUrl, () => {
        // Once new image is loaded, fade out current image (keep Ken Burns active)
        setImageLoaded(false);
        setTimeout(() => {
          // Now reset Ken Burns and switch to new image
          setKenBurnsActive(false);
          setCurrentImage(newImageUrl);
          setImageLoaded(true);
          // Start Ken Burns for new image
          setTimeout(() => setKenBurnsActive(true), 50);
        }, 500);
      });
    } else {
      // First load
      setCurrentImage(newImageUrl);
      preloadImage(newImageUrl, () => {
        setImageLoaded(true);
        setTimeout(() => setKenBurnsActive(true), 50);
      });
    }
  };

  const preloadImage = (imageUrl: string, onLoad: () => void) => {
    // If already cached, call onLoad immediately
    if (imageCache.current[imageUrl]) {
      onLoad();
      return;
    }

    // Preload the image
    const img = new Image();
    img.onload = () => {
      imageCache.current[imageUrl] = true;
      onLoad();
    };
    img.onerror = () => {
      console.error('Failed to load image:', imageUrl);
      onLoad(); // Call anyway to prevent being stuck
    };
    img.src = imageUrl;
  };

  // Error state
  if (error) {
    return (
      <div className="hero-error">
        <div>
          <h2 className="error-title">Unable to load speakers</h2>
          <p className="error-message">{error}</p>
        </div>
      </div>
    );
  }

  // No speakers found - but don't show during initial loading
  if (!loading && !currentSpeaker) {
    return (
      <div className="hero-no-speakers">
        <div>
          <h2 className="no-speakers-title">No Featured Speakers</h2>
          <p className="no-speakers-message">No homepage speakers are currently available.</p>
        </div>
      </div>
    );
  }

  // Don't render anything if still loading or no speaker yet
  if (loading || !currentSpeaker) {
    return (
      <div className="hero" style={{ backgroundColor: 'black' }}>
        {/* Keep the hero shell visible immediately (better perceived performance) */}
        <div
          className="hero-content-container fade-in"
          style={{ opacity: scrollOpacity }}
        >
          <div className="hero-static-content">
            <span className="hero-text-line hero-text-bold">
              Experience Speaks
            </span>
            <span className="hero-text-line hero-text-subtitle">
              World-class leaders from recognizable brands.
            </span>
            <Link to="/browse" style={{ textDecoration: 'none' }}>
              <Button className="hero-cta-button">
                Find the Perfect Speaker
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const biography = getBiographyExcerpt(currentSpeaker);

  return (
    <div className="hero">
      {/* Background Image */}
      <div 
        className={`hero-background ${imageLoaded && currentImage ? 'loaded' : ''} ${kenBurnsActive ? 'ken-burns' : ''}`}
        style={{
          backgroundImage: currentImage ? `url("${currentImage}")` : 'none'
        }}
      />
      
      {/* Upper Speaker Info Section - New location for speaker/company */}
      {showContent && currentSpeaker && (
        <div 
          className={`hero-speaker-info position-${displayedTextPosition}`}
          style={{ opacity: showContent ? scrollOpacity : 0 }}
        >
          <div className={`hero-speaker-content ${displayedTextPosition.includes('left') ? 'align-left' : 'align-right'}`}>
            <Link 
              to={`/speaker/${generateSpeakerSlug(currentSpeaker)}`} 
              className={`hero-speaker-name ${textAnimationClass} ${textVisible && !textAnimationClass ? 'visible' : ''}`}
            >
              {displayedSpeakerName}
            </Link>
            {displayedCompanyName && (
              <span 
                className={`hero-company-name ${textAnimationClass} ${textVisible && !textAnimationClass ? 'visible' : ''}`}
              >
                {displayedCompanyName}
              </span>
            )}
          </div>
        </div>
      )}
      
      {/* Free-floating Hero Content - No box styling */}
      <div 
        className={`hero-content-container ${showContent ? 'fade-in' : ''}`}
        style={{ opacity: showContent ? scrollOpacity : 0 }}
      >
        {/* Static text content - now free-floating */}
        {showContent && (
          <div className="hero-static-content">
            <span className="hero-text-line hero-text-bold">
              Experience Speaks
            </span>
            <span className="hero-text-line hero-text-subtitle">
              World-class leaders from recognizable brands. 
            </span>
            <Link to="/browse" style={{ textDecoration: 'none' }}>
              <Button 
                className="hero-cta-button"
              >
                Find the Perfect Speaker
              </Button>
            </Link>
          </div>
        )}
      </div>

    </div>
  );
};

export default Hero;